"use client";
import cn from "clsx";
import {
    DetailedHTMLProps,
    forwardRef,
    TextareaHTMLAttributes,
    useState,
} from "react";
import { FiAlertCircle } from "react-icons/fi";
import { LoadingSpinner } from "../loading-spinner";
import { RightAdornment } from "./RightAdornment";
import styles from "./Textarea.module.scss";

export type TextareaBodyProps = InnerProps & {
    hasError?: boolean;
    /** Displays a spin component in place of the adornRight while active */
    loading?: boolean;
};
type InnerProps = DetailedHTMLProps<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
>;
export const TextareaBody = forwardRef<HTMLTextAreaElement, TextareaBodyProps>(
    function TextareaBody(
        { hasError, loading, onFocus, onBlur, className, ...props },
        ref,
    ) {
        const [focused, setFocused] = useState(false);

        const classNames = cn([
            styles.container,
            { [styles["container--focused"]]: !props.disabled && focused },
            {
                [styles["container--error"]]:
                    hasError && !props.disabled && !focused,
                [styles["container--not-disabled"]]: !props.disabled,
            },
        ]);

        return (
            <div className={classNames}>
                <textarea
                    {...props}
                    aria-invalid={hasError}
                    className={cn(styles.input, className, {
                        [styles["disabled"]]: props.disabled,
                    })}
                    ref={ref as any}
                    onFocus={(e) => {
                        setFocused(true);
                        onFocus?.(e);
                    }}
                    onBlur={(e) => {
                        setFocused(false);
                        onBlur?.(e);
                    }}
                />
                <RightAdornment>
                    {/* Show spinner if loading, 
                            else show error icon if not disabled and has error
                    */}
                    {loading ? (
                        <LoadingSpinner
                            size="var(--g-size-xxs)"
                            aria-label="Loading"
                            data-testid="textarea-spin"
                        />
                    ) : hasError && !props.disabled ? (
                        <FiAlertCircle size={16} />
                    ) : null}
                </RightAdornment>
            </div>
        );
    },
);
